import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Paper, Grid, Button, Alert, TextField, InputAdornment, IconButton, Chip, Divider, Snackbar } from '@mui/material';
import {
  ContentCopy as CopyIcon, WhatsApp as WhatsAppIcon, OpenInNew as OpenIcon, Android as AndroidIcon,
  Public as PublicIcon, Wifi as WifiIcon, Lock as LockIcon, Refresh as RefreshIcon,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { COLLEGE_NAME } from '../branding';
import ChangePasswordDialog from '../components/ChangePasswordDialog';

const isLocal = (host) => /^(localhost|127\.|10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(host);

function CopyField({ label, value, onCopied }) {
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); } catch (e) {
      const el = document.createElement('textarea'); el.value = value; document.body.appendChild(el); el.select(); document.execCommand('copy'); document.body.removeChild(el);
    }
    onCopied();
  };
  return (
    <TextField fullWidth size="small" label={label} value={value} InputProps={{
      readOnly: true,
      sx: { fontWeight: 600, color: '#004d40' },
      endAdornment: <InputAdornment position="end"><IconButton onClick={copy} edge="end" title="Copy"><CopyIcon /></IconButton></InputAdornment>,
    }} />
  );
}

export default function ConnectApps() {
  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch('/apps/info', { cache: 'no-store' });
      if (!res.ok) throw new Error('bad');
      setInfo(await res.json());
      setLoadError(false);
    } catch (e) { setLoadError(true); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const here = window.location.origin;
  const publicUrl = info?.publicUrl || (isLocal(window.location.hostname) ? '' : here);
  const link = publicUrl;
  const message = `${COLLEGE_NAME}\nOpen the college website: ${link}\nInstall the mobile apps: ${link}/apps\nApp server address: ${link}`;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        <Box>
          <Typography variant="h4">Mobile Apps &amp; Public Link</Typography>
          <Typography variant="body2" color="text.secondary">Share one link so students and faculty can use the website and the apps from any network</Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
      </Box>

      {loadError && <Alert severity="warning" sx={{ mb: 2 }}>Could not read the link details. Open this page through the college web address (not the development server).</Alert>}

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <PublicIcon color="primary" />
              <Typography variant="h6" fontWeight={700}>Public link</Typography>
              {link ? <Chip size="small" color="success" label="Online" /> : <Chip size="small" color="warning" label="Not active" />}
            </Box>
            {link ? (
              <>
                <CopyField label="Website and app server address" value={link} onCopied={() => setCopied(true)} />
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                  <Button variant="contained" startIcon={<WhatsAppIcon />} sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#1da851' } }}
                    href={`https://wa.me/?text=${encodeURIComponent(message)}`} target="_blank" rel="noreferrer">Share on WhatsApp</Button>
                  <Button variant="outlined" startIcon={<OpenIcon />} href={link} target="_blank" rel="noreferrer">Open the link</Button>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Anyone with this link can open the sign-in page from any network. It only works while this college computer is switched on and connected to the internet.
                  The free link changes when the computer restarts; the latest one is always shown here and saved on the Desktop as <b>College Public Link.txt</b>.
                </Typography>
              </>
            ) : (
              <Alert severity="info">
                The public link is not active. Start the system with <b>START_COLLEGE_SYSTEM.bat</b> on the college computer; the link then appears here.
              </Alert>
            )}
            {info?.lanUrl && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <WifiIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2" fontWeight={700}>Same Wi-Fi as the college computer</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">Website: <b>{info.lanUrl}</b> &nbsp;·&nbsp; App server address: <b>{info.lanUrl}</b></Typography>
              </>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, borderRadius: 3, height: '100%', textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>Scan to install the apps</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Point the phone camera here to open the download page</Typography>
            {link ? (
              <Box sx={{ display: 'inline-block', p: 2, bgcolor: '#fff', border: '1px solid #e0e0e0', borderRadius: 3 }}>
                <QRCodeSVG value={`${link}/apps`} size={190} level="M" />
              </Box>
            ) : (
              <Alert severity="info">The QR code appears once the public link is active.</Alert>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Download the apps</Typography>
            <Grid container spacing={2}>
              {(info?.apps || []).map((a) => (
                <Grid item xs={12} sm={6} key={a.key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                    <AndroidIcon sx={{ fontSize: 40, color: '#3ddc84' }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography fontWeight={700}>{a.title}</Typography>
                      <Typography variant="caption" color="text.secondary">{a.text}</Typography>
                    </Box>
                    {a.available
                      ? <Button variant="contained" href={a.url} download>{a.sizeMB} MB</Button>
                      : <Chip label="Not built yet" />}
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 3, mb: 1 }}>Setting up a phone</Typography>
            <Box component="ol" sx={{ m: 0, pl: 3, '& li': { mb: 0.5 }, color: 'text.secondary', fontSize: 14 }}>
              <li>Open the public link followed by <b>/apps</b> on the phone (or scan the QR code), download the app and install it. Allow "Install unknown apps" if Android asks.</li>
              <li>Open the app, tap <b>Server settings</b> on the sign-in screen, paste the <b>server address</b> above and save.</li>
              <li>Sign in with the username and password the college created (Students and Faculty pages in this console).</li>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: 3, borderLeft: '5px solid #f9a825' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <LockIcon color="warning" />
              <Typography variant="h6" fontWeight={700}>Keep the college data safe</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The sign-in page is reachable from the internet, so change the default administrator password now. Sample students and faculty use simple passwords
              (for example <b>student123</b>, <b>faculty123</b>) - reset them from the Students and Faculty pages before real use. After 5 wrong passwords a sign-in is locked for 15 minutes.
            </Typography>
            <Button variant="contained" color="warning" onClick={() => setPwOpen(true)}>Change my password</Button>
          </Paper>
        </Grid>
      </Grid>

      <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
      <Snackbar open={copied} autoHideDuration={1800} onClose={() => setCopied(false)} message="Link copied" />
    </Box>
  );
}
